import { useMemo } from "react";

import { Box, Button, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import TextAreaWithCounter from "~/../../base-components/lib/inputs/TextAreaWithCounter";

import { BUILDER_VERSION } from "../builder/utils";
import {
  ListCuratedSpacesQuery,
  useCreateCuratedSpaceMutation,
  useUpdateCuratedSpaceMutation,
} from "../curation.queries.graphql.generated";

type CreateCuratedSpaceModalProps = {
  curatedSpaces: ListCuratedSpacesQuery["listCuratedSpaces"];
  cloneId: string | null;
  onClose: (curatedSpaceId: string) => void;
};

export const CreateCuratedSpaceModal = (
  props: CreateCuratedSpaceModalProps
) => {
  const { curatedSpaces, cloneId, onClose } = props;

  const [createCuratedSpace, createCuratedSpaceMutationInfo] =
    useCreateCuratedSpaceMutation();
  const [updateCuratedSpace, updateCuratedSpaceMutationInfo] =
    useUpdateCuratedSpaceMutation();

  const isLoading =
    createCuratedSpaceMutationInfo.loading ||
    updateCuratedSpaceMutationInfo.loading;
  const error =
    createCuratedSpaceMutationInfo.error ||
    updateCuratedSpaceMutationInfo.error;

  const form = useForm({
    initialValues: {
      name: "",
      slug: "",
      description: "",
    },
    transformValues: (values) => {
      return {
        name: values.name.trim(),
        slug: values.slug.trim(),
        description: values.description.trim(),
      };
    },
    validate: {
      name: (value) => {
        const trimmedValue = value.trim();
        if (trimmedValue.length < 2) {
          return "Name is too short";
        }
      },
      slug: (value) => {
        const trimmedValue = value.trim();
        if (trimmedValue.length < 2) {
          return "Slug is too short";
        }

        if (curatedSpaces.some((c) => c.slug === trimmedValue)) {
          return "Slug is already taken";
        }

        if (!/^[a-z0-9-]+$/.test(trimmedValue)) {
          return "Slug can only contain lowercase letters, numbers and dashes";
        }
      },
      description: (value) => {
        const trimmedValue = value.trim();

        if (trimmedValue.length < 2) {
          return "Description is too short";
        }

        if (trimmedValue.length > 500) {
          return "Description is too long";
        }
      },
    },
  });

  const clonningCuratedSpace = useMemo(() => {
    if (cloneId == null) return null;

    return curatedSpaces.find((c) => c.slug === cloneId);
  }, [cloneId, curatedSpaces]);

  const handleOnSubmit = async () => {
    console.log("Submit", form.values);

    try {
      const createResult = await createCuratedSpace({
        variables: {
          data: {
            name: form.values.name,
            slug: form.values.slug,
          },
        },
      });

      const createdCuratedSpaceId = createResult.data?.createCuratedSpace.slug;
      if (createdCuratedSpaceId == null) {
        throw new Error("Step 1");
      }

      let css = "";
      let html = "";
      let projectData = "null";

      if (clonningCuratedSpace?.draft != null) {
        css = clonningCuratedSpace.draft.css;
        html = clonningCuratedSpace.draft.html;
        projectData = clonningCuratedSpace.draft.projectData ?? "null";
      }

      const updateResult = await updateCuratedSpace({
        variables: {
          updateCuratedSpaceId: createdCuratedSpaceId,
          data: {
            css,
            html,
            projectData,
            editorVersion: JSON.stringify({
              version: BUILDER_VERSION,
              timestamp: new Date().toISOString(),
            }),
            metadata: {
              name: form.values.name,
              description: form.values.description,
            },
          },
        },
      });

      const updatedCuratedSpaceId = updateResult.data?.updateCuratedSpace.slug;
      if (updatedCuratedSpaceId == null) {
        throw new Error("Step 2");
      }

      console.log("Result", createResult, updateResult);
      onClose(updatedCuratedSpaceId);
    } catch (error) {
      console.error("Error creating curated space", error);
    }
  };

  return (
    <Box>
      <Stack>
        {clonningCuratedSpace && (
          <Stack>
            <Text size={"sm"} fw={500}>
              Template
            </Text>
            <Text px={"md"}>
              {clonningCuratedSpace.metadata?.name ?? "Unknown template"}
            </Text>
          </Stack>
        )}

        <Box component="form" onSubmit={form.onSubmit(handleOnSubmit)}>
          <Stack>
            <TextInput
              label="Slug"
              {...form.getInputProps("slug")}
              icon={
                <Text size={"sm"} c={"dimmed"} w={"100%"} pl={"md"}>
                  highlight.xyz/curated/
                </Text>
              }
              iconWidth={164}
              description="Slug is used in the URL."
              descriptionProps={{ size: "sm", c: "dimmed" }}
            />

            <TextInput
              label="Name"
              {...form.getInputProps("name")}
              description="Name of the curated space. Will be shown in the browser tab and metadata."
              descriptionProps={{ size: "sm", c: "dimmed" }}
            />

            <TextAreaWithCounter
              characterCounter
              maxLength={500}
              minRows={3}
              maxRows={5}
              autosize
              label="Description"
              description="Short description of the curated space. Will be used in the metadata."
              descriptionProps={{ size: "sm", c: "dimmed" }}
              {...form.getInputProps("description")}
            />

            {error?.message && (
              <Text c={"red.5"} fw={500}>
                {error.message}
              </Text>
            )}
            <Button type="submit" loading={isLoading}>
              Create
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};
